import { Link } from "react-router-dom";
import { Clock, Dumbbell, Users, Check, ArrowRight, Award } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { PESISTICA, PESISTICA_BIATHLON } from "@/data/site";

export default function Pesistica() {
  return (
    <div data-testid="pesistica-page">
      <PageHero
        image={PESISTICA.hero}
        overline="Pesistica · Powerlifting · Panca"
        titleLines={["Pesistica &", "Powerlifting"]}
        subtitle={PESISTICA.intro}
      />

      {/* Equipment */}
      <section className="bg-cream py-20 md:py-32" data-testid="pesistica-attrezzature">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="La sala pesi"
            title="Attrezzature professionali"
            subtitle="Uno spazio completo per la pesistica olimpica, la forza e la preparazione atletica in totale sicurezza."
          />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PESISTICA.equipment.map((e) => (
              <StaggerItem key={e.title}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cab/10 text-cab">
                    <Dumbbell className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-extrabold uppercase tracking-tight text-ink">
                    {e.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{e.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Programs */}
      <section className="bg-white py-20 md:py-28" data-testid="pesistica-programmi">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading overline="Come ci si allena" title="Corsi e allenamento libero" />
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:grid-cols-3">
            {PESISTICA.programs.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full bg-white p-8 md:p-10">
                  <span className="font-display text-5xl font-extrabold tracking-tighter text-cab/25">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Biathlon e distensione su panca */}
      <section className="relative overflow-hidden bg-cab-deep py-20 md:py-28" data-testid="pesistica-biathlon">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="mb-6 flex items-center gap-3">
                  <span className="h-px w-10 bg-cream/50" />
                  <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cream/80">
                    {PESISTICA_BIATHLON.eyebrow}
                  </span>
                </div>
                <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.98] tracking-tight text-cream sm:text-5xl">
                  {PESISTICA_BIATHLON.title}
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/80 md:text-lg">
                  {PESISTICA_BIATHLON.text}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.12}>
                <div className="flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 p-12">
                  <Award className="h-24 w-24 text-cab-light" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule + coaches */}
      <section className="bg-cream py-20 md:py-32" data-testid="pesistica-orari">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading overline="Programmazione" title="Orari della sala" />
            <div className="mt-10 overflow-hidden rounded-2xl border border-ink/10">
              {PESISTICA.schedule.map((s, i) => (
                <Reveal key={s.group} delay={i * 0.05}>
                  <div className="flex items-center justify-between gap-4 border-b border-ink/10 bg-white px-6 py-5 last:border-0">
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{s.group}</p>
                      <p className="text-sm text-muted-foreground">{s.days}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-md bg-cab/10 px-4 py-2 font-bold text-cab">
                      <Clock className="h-4 w-4" />
                      {s.time}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading overline="Lo staff tecnico" title="I preparatori atletici" />
            <div className="mt-10 space-y-4">
              {PESISTICA.coaches.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.05}>
                  <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-white p-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-cab text-cream">
                      <Users className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{c.name}</p>
                      <p className="text-sm text-cab">{c.role}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-6 rounded-2xl bg-cab-deep p-7 text-cream">
                <div className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-cab-light" />
                  <p className="text-[15px] leading-relaxed text-cream/85">
                    Ti seguiamo con una <strong className="text-cream">scheda personalizzata</strong> e la
                    supervisione dei preparatori. Prova la prima sessione gratuitamente.
                  </p>
                </div>
                <Link
                  to="/dove-siamo"
                  data-testid="pesistica-cta"
                  className="group mt-5 inline-flex items-center gap-2 rounded-md bg-cream px-6 py-3 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Prenota una prova
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="border-y border-ink/10 bg-cream py-6">
        <Marquee items={["Strappo", "Slancio", "Forza", "Metodo"]} outline />
      </div>
    </div>
  );
}
